import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
  FieldArray
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonForAll from "../../components/common/ButtonForAll";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createTravelActivity,
  getTravelActivityByID,
  updateTravelActivity,
} from "./services/travelActivity.services";
import { clearTravelActivityDetails } from "./travelActivitySlice";
import { getAllActiveSectors } from "../sector/services/sector.services";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import ActivityDetailsArray from "./components/ActivityDetailsArray";

const DAYSARRAY = [
  { label: "Monday", value: "Monday" },
  { label: "Tuesday", value: "Tuesday" },
  { label: "Wednesday", value: "Wednesday" },
  { label: "Thursday", value: "Thursday" },
  { label: "Friday", value: "Friday" },
  { label: "Saturday", value: "Saturday" },
  { label: "Sunday", value: "Sunday" },
];

const CreateTravelActivity: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const TravelActivityID = location.state?.TravelActivityID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.travelActivity.loading);
  const details = useAppSelector((state) => state.travelActivity.TravelActivityDetails);
  const SectorList = useAppSelector((state) => state.sector.SectorList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    if (TravelActivityID) {
      dispatch(getTravelActivityByID(TravelActivityID));
    }
    return () => {
      dispatch(clearTravelActivityDetails());
    };
  }, [TravelActivityID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [details]);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      ID: TravelActivityID ? TravelActivityID : undefined,
      IsActive: values.IsActive === 1,
      DestinationIDs: Array.isArray(values.DestinationIDs)
        ? values.DestinationIDs.join(",")
        : values.DestinationIDs,
      CloseOnDaysofWeek: Array.isArray(values.CloseOnDaysofWeek)
        ? values.CloseOnDaysofWeek.join(",")
        : values.CloseOnDaysofWeek,
      ComplimentoryAge: values.ComplimentoryAge?.toString() || "",
      ActivityDetails: values.ActivityDetails || []
    };

    if (TravelActivityID) {
      const response = await dispatch(updateTravelActivity(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate("/travelactivity");
      }
    } else {
      const response = await dispatch(createTravelActivity(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate("/travelactivity");
      }
    }
  };

  const initialValues = details ? {
    ...details,
    IsActive: details.IsActive ? 1 : 2,
    DestinationIDs: details.DestinationIDs
      ? details.DestinationIDs.split(",").map((id: string) => id.trim())
      : [],
    CloseOnDaysofWeek: details.CloseOnDaysofWeek
      ? details.CloseOnDaysofWeek.split(",").map((day: string) => day.trim())
      : [],
    ComplimentoryAge: details.ComplimentoryAge ? Number(details.ComplimentoryAge) : null,
  } : {
    IsActive: 1,
    DestinationIDs: [],
    CloseOnDaysofWeek: [],
    ComplimentoryAge: null,
    ActivityDetails: [{ Name: "", Slots: "", Duration: 0, DurationType: "", ItineraryDetails: "" }]
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 20 }}>
              <GridLayout
                gap={{ rows: 10, cols: 10 }}
                // style={{ marginRight: 20 }}
                cols={[{ width: "33%" }, { width: "33%" }, { width: "33%" }]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {TravelActivityID ? "Update Travel Activity" : "Add Travel Activity"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Name"
                    label="Activity Name"
                    placeholder="e.g. Kerala Backwaters"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="DestinationIDs"
                    label="Destinations"
                    placeholder="Select Destinations"
                    component={FormMultiSelectionFiled}
                    validator={requiredValidator}
                    astrike={true}
                    options={SectorList?.map((sector: any) => ({
                      value: sector.ID.toString(),
                      label: sector.SectorName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="TouristType"
                    label="Tourist Type"
                    placeholder="e.g. Adult, Child, Group(3-4)"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="ComplimentoryAge"
                    label="Complimentary Age"
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CloseOnDaysofWeek"
                    label="Closed On Days"
                    placeholder="Select Days"
                    component={FormMultiSelectionFiled}
                    options={DAYSARRAY}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="ItineraryDetail"
                    label="Itinerary Summary"
                    component={FormRichTextField}
                    minHeight={150}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    astrike={true}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status?.value,
                      label: status?.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3} style={{ marginTop: 20 }}>
                  <FieldArray name="ActivityDetails" component={ActivityDetailsArray} />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    marginTop: 20,
                  }}
                >
                  <ButtonForAll
                    label={TravelActivityID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/travelactivity")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateTravelActivity;
